/**
 * Demo user definitions for static demo mode (REACT_APP_DEMO_MODE=true)
 *
 * Three hardcoded users matching the lens-demo pattern.
 * All get subscription_plan: 'paid' so the UI treats them as fully subscribed.
 */

import type { User, Permission } from '../types';

// Demo users carry a tenantId field not present on the base User type
type DemoUser = User & { tenantId: string };

const DEMO_CLIENT_ID = 'c0000000-0000-4000-a000-000000000001';
const DEMO_TENANT_ID = 't0000000-0000-4000-a000-000000000001';
const DEMO_PARTNER_ID = 'pt000000-0000-4000-a000-000000000001';

// Base permissions shared by all roles
const BASE_PERMISSIONS: Permission[] = [
  { name: 'projects.view' },
  { name: 'projects.create' },
  { name: 'projects.edit' },
  { name: 'projects.delete' },
  { name: 'projects.manage' },
  { name: 'finance.view' },
  { name: 'finance.edit' },
  { name: 'finance.manage' },
  { name: 'assessments.view' },
  { name: 'assessments.manage' },
  { name: 'users.view' },
  { name: 'users.create' },
  { name: 'users.edit' },
  { name: 'users.manage' },
  { name: 'platformops.manage' },
  { name: 'genaiops.manage' },
  { name: 'agentops.manage' },
  { name: 'dataops.manage' },
];

// Additional permissions for partner/aws roles
const ELEVATED_PERMISSIONS: Permission[] = [
  { name: 'clients.view' },
  { name: 'clients.create' },
  { name: 'clients.edit' },
  { name: 'clients.manage' },
  { name: 'partners.view' },
  { name: 'partners.manage' },
  { name: 'aws_access.view' },
  { name: 'aws_access.manage' },
];

export const DEMO_USERS: Record<string, DemoUser> = {
  'client@example.com': {
    id: 'demo-client-user-001',
    email: 'client@example.com',
    name: 'Client User',
    role: 'client',
    is_anonymous: false,
    is_admin: false,
    subscription_plan: 'paid',
    client_name: 'TechCorp Industries',
    client_id: DEMO_CLIENT_ID,
    client: {
      id: DEMO_CLIENT_ID,
      name: 'TechCorp Industries',
      subscription_plan: 'paid',
      subscription_source: 'organic',
    },
    tenantId: DEMO_TENANT_ID,
    permissions: [
      ...BASE_PERMISSIONS,
      { name: 'clients.view_own' },
    ],
    aws_accessible_modules: ['platformops', 'dataops', 'genaiops', 'agentops'],
  },
  'partner@example.com': {
    id: 'demo-partner-user-001',
    email: 'partner@example.com',
    name: 'Jessica Kim',
    role: 'partner',
    is_anonymous: false,
    is_admin: false,
    subscription_plan: 'paid',
    client_name: null,
    client_id: null,
    partner_id: DEMO_PARTNER_ID,
    partner_name: 'Aivilo',
    tenantId: DEMO_TENANT_ID,
    permissions: [
      ...BASE_PERMISSIONS,
      ...ELEVATED_PERMISSIONS,
    ],
    aws_accessible_modules: ['platformops', 'dataops', 'genaiops', 'agentops'],
  },
  'aws@example.com': {
    id: 'demo-aws-user-001',
    email: 'aws@example.com',
    name: 'AWS Program Manager',
    role: 'aws',
    is_anonymous: false,
    is_admin: false,
    subscription_plan: 'paid',
    client_name: null,
    client_id: null,
    tenantId: DEMO_TENANT_ID,
    permissions: [
      ...BASE_PERMISSIONS,
      ...ELEVATED_PERMISSIONS,
    ],
    aws_accessible_modules: ['platformops', 'dataops', 'genaiops', 'agentops'],
  },
};

// For display on the login page
export const DEMO_USER_LIST: Array<{ email: string; role: string; description: string }> = [
  { email: 'client@example.com', role: 'Client User', description: 'View your specific project progress' },
  { email: 'partner@example.com', role: 'Partner User', description: 'Manage projects and costs for your tenant' },
  { email: 'aws@example.com', role: 'AWS User', description: 'Full visibility across all partners and projects' },
];

export default DEMO_USERS;
